// Shape Graph compilation boundary: the graph walk, static parameter
// evaluation, and text/icon leaf preparation run in the engine
// (crates/wasm/src/shape_compile.rs + crates/graph/src/static_eval.rs).
// TS ships the loaded-font registry, triggers font loads for referenced
// Text nodes, and adopts the compiled result into the shared query cache.

import { compileShapeGraphWasm } from 'fluveria-engine-wasm';
import type { GraphCompilePlan } from '../graph/engine.js';
import { getNodeDataRecord, TEXT_LAYOUT_NODE_TYPES, type ParamStore } from '../graph/nodeDefs.js';
import {
  adoptCompiledShapeGraph,
  ensureBundledFontsLoaded,
  getLoadedFontRegistry,
  resolveBundledFontId,
  type ArcWindRowGrid,
  type ShapeArcQueryData,
} from './shapeEngine.js';
import type { ArcBounds } from './arcSdf.js';
import { isPerfLogEnabled } from '../debug/perfLog.js';

export interface ShapeCompileContext {
  graphId: string | null | undefined;
  params: ParamStore;
  layerSize: [number, number];
  shapeUnit?: 'px' | 'mm';
  pixelsPerShapeUnit: number;
  pixelsPerShapeUnitX?: number;
  pixelsPerShapeUnitY?: number;
  layerSizeAccess?: 'full' | 'deny' | 'width-only' | 'height-only';
  windRowGrid?: ArcWindRowGrid;
  shapes?: Record<string, unknown>;
}

export interface ShapeCompileResult {
  query: ShapeArcQueryData | null;
  layoutBounds: ArcBounds;
  contourBounds: ArcBounds;
  key: string;
  error: string | null;
}

export function compileShapeGraphToShape(plan: GraphCompilePlan, context: ShapeCompileContext): ShapeCompileResult {
  const perf = isPerfLogEnabled();
  const tStart = perf ? performance.now() : 0;
  const result = compileShapeGraphToShapeInner(plan, context);
  if (perf) {
    console.log(`[perf] compileShapeGraph ${context.graphId}: ${(performance.now() - tStart).toFixed(1)}ms key=${result.key.slice(0, 60)}`);
  }
  return result;
}

function compileShapeGraphToShapeInner(plan: GraphCompilePlan, context: ShapeCompileContext): ShapeCompileResult {
  // Fonts referenced by Text nodes load asynchronously; until they arrive the
  // engine produces empty leaves and the compile key changes once loading
  // completes (the font-loaded event triggers a re-render).
  const fontIds = new Set<string>();
  for (const node of plan.nodes) {
    if (TEXT_LAYOUT_NODE_TYPES.has(node.type)) fontIds.add(String(getNodeDataRecord(node).font ?? ''));
  }
  if (fontIds.size > 0) {
    void ensureBundledFontsLoaded([...fontIds].map(id => resolveBundledFontId(id)));
  }
  const result = compileShapeGraphWasm({
    nodes: plan.nodes,
    edges: Object.values(plan.edgeMap),
    graphId: context.graphId ?? undefined,
    params: context.params,
    layerSize: context.layerSize,
    shapeUnit: context.shapeUnit ?? 'px',
    pixelsPerShapeUnit: context.pixelsPerShapeUnit,
    pixelsPerShapeUnitX: context.pixelsPerShapeUnitX,
    pixelsPerShapeUnitY: context.pixelsPerShapeUnitY,
    layerSizeAccess: context.layerSizeAccess ?? 'full',
    windRowGrid: context.windRowGrid,
    ...(context.shapes ? { shapes: context.shapes } : {}),
    fonts: getLoadedFontRegistry(),
  });
  const error = result.error() ?? null;
  const adopted = adoptCompiledShapeGraph(result);
  return {
    query: adopted.query,
    layoutBounds: adopted.layoutBounds,
    contourBounds: adopted.contourBounds,
    key: adopted.key,
    error,
  };
}
